import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  TOUR_STEPS,
  TOUR_STEP_KEY,
  TOUR_SESSION_SHOWN_KEY,
  TOUR_STORAGE_KEY,
  TOUR_PENDING_FIRST_SESSION_KEY,
  TOUR_FIRST_TIME_DONE_EVENT,
  TOUR_OPEN_EVENT,
} from "../../constants/tourSteps";
import { appStorage } from "../../utils/appStorage";

const useIsMobileLayout = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isMobile;
};

export function markTourShownThisSession() {
  try {
    appStorage.setItem(TOUR_SESSION_SHOWN_KEY, "true");
    appStorage.removeItem(TOUR_STEP_KEY);
  } catch (error) {
    // ignore storage failures
  }
}

export function clearTourSessionFlag() {
  try {
    appStorage.removeItem(TOUR_SESSION_SHOWN_KEY);
    appStorage.removeItem(TOUR_STEP_KEY);
  } catch (error) {
    // ignore storage failures
  }
}

const getTourUserId = () => {
  try {
    return appStorage.getItem("user_id") || "";
  } catch (error) {
    return "";
  }
};

export const getFirstTimeTourStorageKey = () => {
  const userId = getTourUserId();
  return userId ? `${TOUR_STORAGE_KEY}_${userId}` : TOUR_STORAGE_KEY;
};

export function markTourPendingFirstOpen() {
  try {
    appStorage.setItem(TOUR_PENDING_FIRST_SESSION_KEY, "true");
  } catch (error) {
    // ignore storage failures
  }
}

export function clearTourPendingFirstOpen() {
  try {
    appStorage.removeItem(TOUR_PENDING_FIRST_SESSION_KEY);
  } catch (error) {
    // ignore storage failures
  }
}

export function isTourPendingFirstOpen() {
  try {
    return appStorage.getItem(TOUR_PENDING_FIRST_SESSION_KEY) === "true";
  } catch (error) {
    return false;
  }
}

export function markFirstTimeTourCompleted() {
  try {
    localStorage.setItem(getFirstTimeTourStorageKey(), "true");
    clearTourPendingFirstOpen();
  } catch (error) {
    // ignore storage failures
  }
}

export function isFirstTimeTourCompleted() {
  try {
    return localStorage.getItem(getFirstTimeTourStorageKey()) === "true";
  } catch (error) {
    return false;
  }
}

export function requestFirstTimeTourOpen() {
  markTourPendingFirstOpen();
  try {
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(TOUR_OPEN_EVENT, { detail: { firstTime: true } })
      );
    }, 50);
  } catch (error) {
    // ignore dispatch failures
  }
}

export function notifyFirstTimeTourDone() {
  try {
    window.dispatchEvent(new CustomEvent(TOUR_FIRST_TIME_DONE_EVENT));
  } catch (error) {
    // ignore dispatch failures
  }
}

export function isTourShownThisSession() {
  try {
    return appStorage.getItem(TOUR_SESSION_SHOWN_KEY) === "true";
  } catch (error) {
    return false;
  }
}

const getStoredStep = () => {
  try {
    const raw = appStorage.getItem(TOUR_STEP_KEY);
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return 0;
    return Math.min(Math.max(parsed, 0), TOUR_STEPS.length - 1);
  } catch (error) {
    return 0;
  }
};

const setStoredStep = (step) => {
  try {
    appStorage.setItem(TOUR_STEP_KEY, String(step));
  } catch (error) {
    // ignore storage failures
  }
};

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
    <path
      d="M3.5 8.2l2.8 2.8 6.2-6.4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
    <path
      d="M4 10h12m0 0l-4-4m4 4l-4 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function TourHero({ step }) {
  return (
    <div className="relative overflow-hidden px-4 pb-2 pt-5 sm:px-10 sm:pt-7">
      <div
        className="pointer-events-none absolute -left-10 -top-10 h-32 w-32 rounded-full blur-3xl sm:h-44 sm:w-44"
        style={{ background: step.glow }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-6 top-12 h-28 w-28 rounded-full bg-[#59C5FF]/15 blur-3xl sm:top-16 sm:h-36 sm:w-36"
        aria-hidden="true"
      />

      <div className="relative flex items-center justify-between gap-3">
        <span
          className={`inline-flex items-center rounded-full bg-gradient-to-r ${step.accent} px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white shadow-sm sm:px-3 sm:text-[11px]`}
        >
          {step.tag}
        </span>
      </div>

      <div className="relative mx-auto mt-3 flex w-full max-w-[380px] items-center justify-center">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -right-2 top-2 h-16 w-16 rounded-full bg-white/25 blur-[2px] sm:h-24 sm:w-24" />
          <div className="absolute bottom-4 left-0 h-14 w-14 rounded-full bg-[#E8F4FF]/40 blur-[1px] sm:h-20 sm:w-20" />
        </div>

        <div className="tour-mirror-panel relative w-full rounded-[28px] p-2.5 sm:rounded-[40px] sm:p-3.5">
          <div
            className="pointer-events-none absolute inset-0 rounded-[28px] bg-gradient-to-br from-white/45 via-white/15 to-white/5 sm:rounded-[40px]"
            aria-hidden="true"
          />

          <div className="relative flex h-[132px] w-full items-center justify-center overflow-hidden rounded-[22px] bg-white p-4 shadow-[0_16px_48px_rgba(35,16,83,0.14)] ring-1 ring-white/80 sm:h-[200px] sm:rounded-[30px] sm:p-7">
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-white to-[#FAF8FF]"
              aria-hidden="true"
            />
            <img
              src={step.image}
              alt=""
              className="relative z-[1] h-full w-full object-contain"
              loading="eager"
              draggable="false"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TourGuideModal({
  open,
  onClose,
  initialStep = 0,
  isReplay = false,
  isFirstTime = false,
}) {
  const isMobile = useIsMobileLayout();
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef(null);
  const [stepIndex, setStepIndex] = useState(initialStep);

  const step = TOUR_STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === TOUR_STEPS.length - 1;
  const progress = ((stepIndex + 1) / TOUR_STEPS.length) * 100;

  useEffect(() => {
    if (!open) return undefined;
    setStepIndex(isReplay ? 0 : getStoredStep() || initialStep);
    return undefined;
  }, [open, initialStep, isReplay]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleSkip();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      dialogRef.current?.focus();
    }
  }, [open, stepIndex]);

  const handleClose = useCallback(() => {
    markTourShownThisSession();
    if (isFirstTime && !isReplay) {
      markFirstTimeTourCompleted();
      notifyFirstTimeTourDone();
    }
    onClose?.();
  }, [isFirstTime, isReplay, onClose]);

  const handleSkip = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const goToStep = useCallback((next) => {
    setStepIndex(next);
    setStoredStep(next);
  }, []);

  const handleNext = useCallback(() => {
    if (isLast) {
      handleClose();
      return;
    }
    goToStep(stepIndex + 1);
  }, [goToStep, handleClose, isLast, stepIndex]);

  const handleBack = useCallback(() => {
    goToStep(Math.max(stepIndex - 1, 0));
  }, [goToStep, stepIndex]);

  if (!open || !step) return null;

  const layoutHint =
    step.showMobileHint && (isMobile ? step.mobileHint : step.desktopHint);

  return (
    <div
      className="fixed inset-0 z-[29990] flex items-end justify-center bg-[#12082A]/55 px-4 pb-4 pt-0 backdrop-blur-md sm:items-center sm:p-4"
      onClick={handleSkip}
      role="presentation"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative flex max-h-[92dvh] w-full max-w-[560px] flex-col overflow-hidden rounded-[24px] shadow-[0_32px_100px_rgba(35,16,83,0.35)] outline-none sm:max-h-[95dvh] sm:rounded-[32px]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={`relative shrink-0 bg-gradient-to-br ${step.accent} text-white`}>
          <button
            type="button"
            onClick={handleSkip}
            aria-label="Close tour"
            className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md hover:bg-white/25 sm:right-4 sm:top-4"
          >
            <CloseIcon />
          </button>

          <TourHero step={step} />

          <div className="px-4 pb-3 sm:px-10 sm:pb-4">
            <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.1em] text-white/75 sm:text-[11px]">
              <span>Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/20">
              <div
                className="h-full rounded-full bg-white"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-4 px-6 sm:px-10 ">
            <div>
              <div className="mt-1 flex flex-col gap-2 sm:mt-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <h2
                  id={titleId}
                  className="text-[22px] font-extrabold leading-tight tracking-tight text-[#14151F] sm:text-[30px]"
                >
                  {step.title}
                </h2>
                <div className="inline-flex shrink-0 items-center self-start rounded-full bg-[#F3F0FF] px-3 py-1.5 text-[11px] font-bold text-[#6C45FF] sm:py-2 sm:text-xs">
                  Step {stepIndex + 1} of {TOUR_STEPS.length}
                </div>
              </div>
              <p
                id={descId}
                className="mt-2 text-sm leading-6 text-[#6B7080] sm:mt-1.5 sm:text-base sm:leading-7"
              >
                {step.description}
              </p>

              {layoutHint && (
                <div className="mt-3 flex items-start gap-2.5 rounded-2xl border border-[#EEE8FF] bg-gradient-to-r from-[#FAF8FF] to-[#F5FAFF] px-3 py-2.5 sm:mt-4 sm:gap-3 sm:px-4 sm:py-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#6C45FF]/10 text-sm">
                    💡
                  </span>
                  <p className="text-xs font-medium leading-5 text-[#5B4F8C] sm:text-sm sm:leading-6">
                    {layoutHint}
                  </p>
                </div>
              )}

              {Array.isArray(step.bullets) && step.bullets.length > 0 && (
                <ul className="mt-3 space-y-2 sm:mt-2 sm:space-y-2.5">
                  {step.bullets.map((bullet) => (
                    <li
                      key={bullet}
                      className="flex items-start gap-2.5 rounded-2xl border border-[#F0EDFF] bg-[#FBFAFF] px-3 py-2 sm:items-center sm:gap-3 sm:py-1.5"
                    >
                      <span
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br sm:mt-0 sm:h-7 sm:w-7 ${step.accent} text-white shadow-sm`}
                      >
                        <CheckIcon />
                      </span>
                      <span className="text-xs font-medium leading-5 text-[#3D4050] sm:text-sm sm:leading-6">
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="shrink-0 border-t border-[#F0EDFF] bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-10 sm:py-4">
            <div className="flex flex-col gap-2.5 sm:gap-3">
              <div className="flex items-center gap-2">
                {!isFirst && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex h-11 flex-1 items-center justify-center rounded-2xl border border-[#E8E4F5] bg-white text-sm font-semibold text-[#4B4F5E] hover:bg-[#FAFAFC] sm:h-10"
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNext}
                  className={`flex h-11 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r ${step.accent} px-5 text-sm font-bold text-white shadow-[0_14px_36px_rgba(108,69,255,0.28)] hover:brightness-105 sm:h-10 sm:px-6 ${isFirst ? "w-full" : "flex-[1.4]"}`}
                >
                  <span>{isLast ? "Get Started" : "Continue"}</span>
                  {!isLast && <ArrowIcon />}
                </button>
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="py-1 text-xs font-medium text-[#9AA0B0] hover:text-[#6C45FF]"
                >
                  Skip tour
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
